import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PageResponse } from '@payer-admin-ui/api/src/utils/repository/PageResponse';
import { ClientNotificationResponse } from '@payer-admin-ui/api/src/notifications/models';
import { sleep } from 'utils/javascript';
import { notificationAPI } from './notificationAPI';
import type { MerchantAdminDispatch, MerchantAdminState } from './store';

const NOTIFICATIONS_POLL_INTERVAL = 7000;

export type NotificationsListState = {
  isFetching: boolean;
  error: unknown | null;
  totalCount: number | null;
  page: number;
  pageSize: number;
  channel: string | null;
  sort: string | null;
  items: ClientNotificationResponse[];
};

export type NotificationsState = {
  listView: NotificationsListState;
};

const initialState: NotificationsState = {
  listView: {
    isFetching: false,
    error: null,
    totalCount: null,
    page: 0,
    pageSize: 50,
    channel: null,
    sort: null,
    items: [],
  },
};

export const fetchNotificationsPage = createAsyncThunk<
  PageResponse<ClientNotificationResponse>,
  void,
  { state: MerchantAdminState }
>('notifications/fetchPage', async (_, { getState }) => {
  const { page, pageSize, channel, sort } = getState().notifications.listView;
  const response = await notificationAPI.fetchPage({
    page,
    pageSize,
    channel: channel || undefined,
    sort: sort || undefined,
  });
  return response;
});

export const startNotificationsPolling = createAsyncThunk<
  void,
  void,
  { state: MerchantAdminState; dispatch: MerchantAdminDispatch }
>('notifications/startPolling', async (_, { dispatch, getState, signal }) => {
  while (!signal.aborted) {
    if (!getState().session.client) return;

    const { isFetching } = getState().notifications.listView;
    if (!isFetching) await dispatch(fetchNotificationsPage());

    await sleep(NOTIFICATIONS_POLL_INTERVAL);
  }
});

export const markNotificationSeen = createAsyncThunk<string, string>('notifications/markSeen', async (id: string) => {
  await notificationAPI.markSeen(id);
  return id;
});

export const markNotificationHandled = createAsyncThunk<string, string>(
  'notifications/markHandled',
  async (id: string) => {
    await notificationAPI.markHandled(id);
    return id;
  },
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setPage: (state, { payload }: PayloadAction<number>) => {
      state.listView.page = payload;
    },
    setPageSize: (state, { payload }: PayloadAction<number>) => {
      state.listView.pageSize = payload;
      state.listView.page = 0;
    },
    setChannel: (state, { payload }: PayloadAction<string | null>) => {
      state.listView.channel = payload;
      state.listView.page = 0;
    },
    setSort: (state, { payload }: PayloadAction<string | null>) => {
      state.listView.sort = payload;
      state.listView.page = 0;
    },
    resetError: state => {
      state.listView.error = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchNotificationsPage.pending, state => {
      state.listView.isFetching = true;
      state.listView.error = null;
    });
    builder.addCase(fetchNotificationsPage.fulfilled, (state, { payload }) => {
      state.listView.isFetching = false;
      state.listView.items = payload.contents;
      state.listView.totalCount = payload.totalCount;
    });
    builder.addCase(fetchNotificationsPage.rejected, (state, { error }) => {
      state.listView.isFetching = false;
      state.listView.items = [];
      state.listView.totalCount = 0;
      state.listView.error = error;
    });
    builder.addCase(markNotificationSeen.fulfilled, (state, { payload: id }) => {
      const item = state.listView.items.find(n => n.id === id);
      if (item) item.seen = true;
    });
    builder.addCase(markNotificationHandled.fulfilled, (state, { payload: id }) => {
      const item = state.listView.items.find(n => n.id === id);
      if (item) item.handled = true;
    });
  },
});

export const {
  actions: { setPage, setPageSize, setChannel, setSort, resetError },
  reducer: notificationsReducer,
} = notificationsSlice;
